/**
 * Open a PDF for PREVIEW in a new browser tab instead of force-downloading it. The native PDF viewer
 * shows it inline WITH its own download / print buttons, so the user downloads only if they want to.
 *
 * The tab is opened SYNCHRONOUSLY as the first thing on the click (before any `await`), so it isn't
 * caught by the pop-up blocker; the fetched blob is then loaded into that already-open tab.
 *
 * `fetchBlob` returns the axios blob response (`responseType: 'blob'`) or a `Blob` directly. Re-throws
 * on failure so the caller can show a friendly toast (e.g. via `readDownloadError`).
 */
export async function previewPdf(fetchBlob: () => Promise<{ data: Blob } | Blob>): Promise<void> {
  const w = window.open('', '_blank');
  // A tiny placeholder so the freshly-opened tab isn't a jarring blank while the PDF is fetched.
  try { w?.document.write('<title>Loading…</title><body style="margin:0;font:14px system-ui;color:#556;display:grid;place-items:center;height:100vh">Preparing PDF…</body>'); } catch { /* cross-origin/no-doc — ignore */ }
  try {
    const res = await fetchBlob();
    const data = res instanceof Blob ? res : res.data;
    const href = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
    if (w) w.location.href = href;
    else window.open(href, '_blank'); // pop-up was blocked at open — best-effort direct open
    // Free the blob after the tab has loaded it — revoking this late never unloads the already-rendered
    // PDF, but WITHOUT it every preview leaks the full PDF bytes for the life of the opener tab.
    setTimeout(() => URL.revokeObjectURL(href), 60_000);
  } catch (e) {
    w?.close();
    throw e;
  }
}
